import { useState } from "react";
import { Modal } from "./Modal";
import { Input } from "./Input";
import { Button } from "./Button";
import { mailingListsAPI } from "../../api/endpoints";
import type { MailingList, MailingListContact, Band } from "../../types";

// --- Create/Edit Mailing List Modal ---
export function MailingListFormModal({
  mailingList,
  bands,
  initialContacts = [],
  onClose,
  onSaved,
}: {
  mailingList?: MailingList;
  bands: Band[];
  /** Contacts to pre-populate when creating a new list (ignored when editing). */
  initialContacts?: MailingListContact[];
  onClose: () => void;
  onSaved: (saved?: MailingList) => void;
}) {
  const [name, setName] = useState(mailingList?.name ?? "");
  const [description, setDescription] = useState(mailingList?.description ?? "");
  const [selectedBandIds, setSelectedBandIds] = useState<string[]>(mailingList?.band_ids ?? []);
  const [isSaving, setIsSaving] = useState(false);

  const isEditing = !!mailingList;

  const toggleBand = (bandId: string) => {
    setSelectedBandIds((prev) =>
      prev.includes(bandId) ? prev.filter((id) => id !== bandId) : [...prev, bandId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSaving(true);
    try {
      let saved: MailingList;
      if (isEditing) {
        const res = await mailingListsAPI.update(mailingList._id, {
          name: name.trim(),
          description: description.trim() || undefined,
          band_ids: selectedBandIds,
        });
        saved = res.data;
      } else {
        const res = await mailingListsAPI.create({
          name: name.trim(),
          description: description.trim() || undefined,
          contacts: initialContacts,
          band_ids: selectedBandIds,
        });
        saved = res.data;
      }
      onSaved(saved);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEditing ? "Edit Mailing List" : "New Mailing List"}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {!isEditing && initialContacts.length > 0 && (
          <p className="text-sm text-dark-text-secondary bg-dark-bg border border-dark-border rounded-lg px-3 py-2">
            {initialContacts.length} contact{initialContacts.length !== 1 ? "s" : ""} will be added to this list.
          </p>
        )}
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Jazz venues France"
          autoFocus
          required
        />
        <Input
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="A short description..."
        />
        {bands.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-1">
              Associated Bands
            </label>
            <div className="flex flex-wrap gap-2">
              {bands.map((band) => (
                <button
                  key={band._id}
                  type="button"
                  onClick={() => toggleBand(band._id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    selectedBandIds.includes(band._id)
                      ? "bg-primary-900/30 text-primary-400 border border-primary-700/50"
                      : "bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border"
                  }`}
                >
                  {selectedBandIds.includes(band._id) ? "✓ " : "+ "}
                  {band.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={isSaving}>
            {isEditing ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
